"use client";

import { useCallback, useEffect, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";
import type { MoveDisplay } from "@/engine/move-learn";

// Pure / presentational. All data arrives via props; the single outcome —
// "replace move at index N" or "skip" — leaves via `onResolve`. Pass a move
// index (0..currentMoves.length-1) to replace that slot; pass `null` to skip.
// This component imports NO game store: the shell mounts it and applies the
// resolution to the party.
type Props = {
  /** The Surggie's display name (nickname or species name). */
  surggieName: string;
  /** The move being offered. */
  newMove: MoveDisplay;
  /** The Surggie's current (full) moveset — expected length 4. */
  currentMoves: MoveDisplay[];
  /**
   * Resolution callback. `replacedIndex` is the index of the move to forget
   * (the new move takes its slot), or `null` to skip learning entirely.
   */
  onResolve: (replacedIndex: number | null) => void;
};

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";

const TYPE_COLORS: Record<string, string> = {
  sales: "#b8704a",
  tech: "#4a7eb8",
  finance: "#c5a83a",
  creative: "#a04ab8",
  ops: "#7a8a4a",
  normal: "#8a8a8a",
};

function typeColor(type: string): string {
  return TYPE_COLORS[type.toLowerCase()] ?? TYPE_COLORS.normal;
}

function typeAbbrev(type: string): string {
  return type.slice(0, 4).toUpperCase();
}

type SurggiesAudio = {
  playSfx?: (name: string) => void;
};

function playSfx(name: string): void {
  if (typeof window === "undefined") return;
  const audio = (window as unknown as { surggiesAudio?: SurggiesAudio })
    .surggiesAudio;
  audio?.playSfx?.(name);
}

type Phase = "confirm" | "choose" | "skipped";

// YES = replace, NO = skip. NO is default focus.
const YES_NO: ReadonlyArray<"yes" | "no"> = ["yes", "no"];

interface MoveTileProps {
  move: MoveDisplay;
  active: boolean;
  isNew: boolean;
  onHover: () => void;
  onClick: () => void;
}

function MoveTile({ move, active, isNew, onHover, onClick }: MoveTileProps) {
  const tileBg = active ? INK : CREAM;
  const tileFg = active ? CREAM : INK;
  const powerLabel = move.power > 0 ? `Power: ${move.power}` : "Power: \u2014";

  return (
    <button
      type="button"
      role="menuitem"
      onMouseEnter={onHover}
      onClick={onClick}
      className="relative font-mono font-bold uppercase tracking-widest text-left"
      style={{
        backgroundColor: tileBg,
        color: tileFg,
        border: `2px solid ${INK}`,
        padding: "0.625rem 0.75rem 0.625rem 1.75rem",
        minHeight: "4.25rem",
        cursor: "pointer",
        transition: "none",
        imageRendering: "pixelated",
        display: "flex",
        flexDirection: "column",
        gap: "0.375rem",
        width: "100%",
      }}
    >
      {active && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: "0.5rem",
            top: "0.75rem",
            color: CREAM,
            fontSize: "0.95rem",
            lineHeight: 1,
          }}
        >
          &#9654;
        </span>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>
          {move.name}
          {isNew && (
            <span
              style={{
                marginLeft: "0.5rem",
                fontSize: "0.65rem",
                letterSpacing: "0.1em",
                color: active ? CREAM : WALNUT,
              }}
            >
              (NEW!)
            </span>
          )}
        </span>
        <span
          aria-label={`Type ${move.type}`}
          style={{
            backgroundColor: typeColor(move.type),
            color: CREAM,
            border: `2px solid ${INK}`,
            padding: "0.125rem 0.375rem",
            fontSize: "0.65rem",
            lineHeight: 1,
            letterSpacing: "0.1em",
            imageRendering: "pixelated",
          }}
        >
          {typeAbbrev(move.type)}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
          fontSize: "0.65rem",
          letterSpacing: "0.1em",
          marginTop: "auto",
        }}
      >
        <span>{`PP ${move.pp}`}</span>
        <span>{powerLabel}</span>
      </div>
    </button>
  );
}

export function MoveLearnPrompt({
  surggieName,
  newMove,
  currentMoves,
  onResolve,
}: Props) {
  const [phase, setPhase] = useState<Phase>("confirm");
  // 0 = YES, 1 = NO. Default to NO.
  const [yesNoIndex, setYesNoIndex] = useState<number>(1);
  // Slot cursor: 0..3 = current moves, 4 = new move (which acts as skip).
  const [slotIndex, setSlotIndex] = useState<number>(0);

  const confirmYes = useCallback(() => {
    playSfx("menu_select");
    setPhase("choose");
    setSlotIndex(0);
  }, []);

  const confirmNo = useCallback(() => {
    playSfx("menu_back");
    setPhase("skipped");
  }, []);

  const handleChooseSlot = useCallback(
    (index: number) => {
      if (index >= 0 && index < currentMoves.length) {
        playSfx("menu_select");
        onResolve(index);
      } else {
        // New-move slot = skip.
        playSfx("menu_back");
        onResolve(null);
      }
    },
    [currentMoves.length, onResolve],
  );

  // Keyboard handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;

      if (phase === "confirm") {
        if (k === "ArrowLeft" || k === "ArrowRight" || k === "ArrowUp" || k === "ArrowDown") {
          e.preventDefault();
          playSfx("menu_move");
          setYesNoIndex((i) => (i === 0 ? 1 : 0));
          return;
        }
        if (k === "z" || k === "Z" || k === "Enter") {
          e.preventDefault();
          if (YES_NO[yesNoIndex] === "yes") confirmYes();
          else confirmNo();
          return;
        }
        if (k === "x" || k === "X" || k === "Escape") {
          e.preventDefault();
          confirmNo();
          return;
        }
        return;
      }

      if (phase === "choose") {
        const totalSlots = currentMoves.length + 1; // +1 for the new-move tile

        if (k === "ArrowLeft") {
          e.preventDefault();
          playSfx("menu_move");
          setSlotIndex((i) => {
            // Within the 2x2 current grid, move left if possible.
            if (i < currentMoves.length) {
              const col = i % 2;
              if (col > 0) return i - 1;
            }
            return i;
          });
          return;
        }
        if (k === "ArrowRight") {
          e.preventDefault();
          playSfx("menu_move");
          setSlotIndex((i) => {
            if (i < currentMoves.length) {
              const col = i % 2;
              if (col < 1 && i + 1 < currentMoves.length) return i + 1;
            }
            return i;
          });
          return;
        }
        if (k === "ArrowUp") {
          e.preventDefault();
          playSfx("menu_move");
          setSlotIndex((i) => {
            // From new-move tile go to bottom row of grid.
            if (i === currentMoves.length) {
              return Math.min(currentMoves.length - 1, 2);
            }
            // From bottom row of grid go to top row.
            if (i >= 2 && i < currentMoves.length) {
              return i - 2;
            }
            return i;
          });
          return;
        }
        if (k === "ArrowDown") {
          e.preventDefault();
          playSfx("menu_move");
          setSlotIndex((i) => {
            // From top row of grid go to bottom row.
            if (i < 2 && i + 2 < currentMoves.length) {
              return i + 2;
            }
            // From any grid slot go to new-move tile.
            if (i < currentMoves.length) {
              return currentMoves.length;
            }
            return i;
          });
          return;
        }
        if (k === "z" || k === "Z" || k === "Enter") {
          e.preventDefault();
          handleChooseSlot(slotIndex);
          return;
        }
        if (k === "x" || k === "X" || k === "Escape") {
          e.preventDefault();
          playSfx("menu_back");
          setPhase("confirm");
          setYesNoIndex(1);
          return;
        }
        // Clamp slotIndex if currentMoves length somehow changes.
        if (slotIndex > totalSlots - 1) {
          setSlotIndex(totalSlots - 1);
        }
        return;
      }

      // phase === "skipped"
      if (k === "z" || k === "Z" || k === "Enter" || k === "x" || k === "X" || k === "Escape") {
        e.preventDefault();
        playSfx("menu_select");
        onResolve(null);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    phase,
    yesNoIndex,
    slotIndex,
    currentMoves.length,
    confirmYes,
    confirmNo,
    handleChooseSlot,
    onResolve,
  ]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Learn new move"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(26, 26, 26, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 70,
        imageRendering: "pixelated",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      <div
        style={{
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
          padding: "1.25rem 1.5rem",
          maxWidth: 640,
          width: "92%",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {phase === "confirm" && (
          <ConfirmPanel
            speciesNickname={surggieName}
            newMoveName={newMove.name}
            yesNoIndex={yesNoIndex}
            onHoverYes={() => setYesNoIndex(0)}
            onHoverNo={() => setYesNoIndex(1)}
            onClickYes={confirmYes}
            onClickNo={confirmNo}
          />
        )}

        {phase === "choose" && (
          <ChoosePanel
            speciesNickname={surggieName}
            newMove={newMove}
            currentMoves={currentMoves}
            slotIndex={slotIndex}
            onHover={(i) => setSlotIndex(i)}
            onClick={(i) => handleChooseSlot(i)}
          />
        )}

        {phase === "skipped" && (
          <SkippedPanel
            speciesNickname={surggieName}
            newMoveName={newMove.name}
            onContinue={() => {
              playSfx("menu_select");
              onResolve(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default MoveLearnPrompt;

// --- Subpanels ---------------------------------------------------------------

interface ConfirmPanelProps {
  speciesNickname: string;
  newMoveName: string;
  yesNoIndex: number;
  onHoverYes: () => void;
  onHoverNo: () => void;
  onClickYes: () => void;
  onClickNo: () => void;
}

function ConfirmPanel({
  speciesNickname,
  newMoveName,
  yesNoIndex,
  onHoverYes,
  onHoverNo,
  onClickYes,
  onClickNo,
}: ConfirmPanelProps) {
  return (
    <>
      <PixelText
        as="div"
        glow="none"
        size="md"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        {`${speciesNickname} wants to learn ${newMoveName}!`}
      </PixelText>
      <div
        style={{
          color: INK,
          fontWeight: 700,
          fontSize: "0.95rem",
          lineHeight: 1.45,
        }}
      >
        {`But ${speciesNickname} already knows 4 moves. Forget a move to make room?`}
      </div>

      <div
        role="menu"
        aria-label="Confirm forget a move"
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-end",
          marginTop: "0.5rem",
        }}
      >
        {(["yes", "no"] as const).map((label, i) => {
          const selected = yesNoIndex === i;
          return (
            <button
              key={label}
              type="button"
              role="menuitem"
              aria-selected={selected}
              onMouseEnter={i === 0 ? onHoverYes : onHoverNo}
              onClick={i === 0 ? onClickYes : onClickNo}
              className="font-mono font-bold uppercase tracking-widest"
              style={{
                backgroundColor: selected ? INK : CREAM,
                color: selected ? CREAM : INK,
                border: `2px solid ${INK}`,
                padding: "0.5rem 1.25rem",
                minWidth: 84,
                cursor: "pointer",
                transition: "none",
                imageRendering: "pixelated",
                fontSize: "0.95rem",
                letterSpacing: "0.1em",
              }}
            >
              {label.toUpperCase()}
            </button>
          );
        })}
      </div>
    </>
  );
}

interface ChoosePanelProps {
  speciesNickname: string;
  newMove: MoveDisplay;
  currentMoves: MoveDisplay[];
  slotIndex: number;
  onHover: (index: number) => void;
  onClick: (index: number) => void;
}

function ChoosePanel({
  speciesNickname,
  newMove,
  currentMoves,
  slotIndex,
  onHover,
  onClick,
}: ChoosePanelProps) {
  return (
    <>
      <PixelText
        as="div"
        glow="none"
        size="md"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        {`Which move should ${speciesNickname} forget?`}
      </PixelText>
      <div
        style={{
          color: WALNUT,
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Pick the new move to skip learning it.
      </div>

      <div
        role="menu"
        aria-label="Pick a move to forget"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.5rem",
        }}
      >
        {currentMoves.map((m, i) => (
          <MoveTile
            key={`current-${i}-${m.id}`}
            move={m}
            active={slotIndex === i}
            isNew={false}
            onHover={() => onHover(i)}
            onClick={() => onClick(i)}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginTop: "0.25rem",
        }}
      >
        <div
          aria-hidden
          style={{
            flex: 1,
            height: 2,
            backgroundColor: SAND,
          }}
        />
        <span
          style={{
            color: WALNUT,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontWeight: 700,
            fontSize: "0.7rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          Or
        </span>
        <div
          aria-hidden
          style={{
            flex: 1,
            height: 2,
            backgroundColor: SAND,
          }}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "0.5rem",
        }}
      >
        <MoveTile
          move={newMove}
          active={slotIndex === currentMoves.length}
          isNew
          onHover={() => onHover(currentMoves.length)}
          onClick={() => onClick(currentMoves.length)}
        />
      </div>
    </>
  );
}

interface SkippedPanelProps {
  speciesNickname: string;
  newMoveName: string;
  onContinue: () => void;
}

function SkippedPanel({
  speciesNickname,
  newMoveName,
  onContinue,
}: SkippedPanelProps) {
  return (
    <>
      <PixelText
        as="div"
        glow="none"
        size="md"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        {`${speciesNickname} did not learn ${newMoveName}.`}
      </PixelText>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "0.5rem",
        }}
      >
        <button
          type="button"
          onClick={onContinue}
          className="font-mono font-bold uppercase tracking-widest"
          style={{
            backgroundColor: INK,
            color: CREAM,
            border: `2px solid ${INK}`,
            padding: "0.5rem 1.25rem",
            minWidth: 84,
            cursor: "pointer",
            transition: "none",
            imageRendering: "pixelated",
            fontSize: "0.95rem",
            letterSpacing: "0.1em",
          }}
        >
          OK
        </button>
      </div>
    </>
  );
}
